import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import dayjs, { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { getAllBranch } from "../../services";
import { Branch } from "../../types/user.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import {
  QR_PAYMENT_STATUS_LABEL,
  QrPaymentStatus,
} from "../../types/qr-payment.type";
import DateRangePicker, {
  DEFAULT_LOOKBACK_YEARS,
  allTimePreset,
  defaultPresetGroups,
} from "../common/DateRangePicker";
import FilterField, { FILTER_ROW_BOTTOM_SPACING } from "../SaleDepositReport/FilterField";

/**
 * The three work queues, named rather than left for the reader to assemble from a status and
 * a null check.
 *
 * The first two mean "the bank has money the books do not account for, and nobody has picked
 * it up". The third is the opposite kind of row and is listed last for that reason: someone
 * HAS picked it up — a refund is ordered and accounting are working it — and it is here so
 * the money can be found again while it sits between the two sets of books.
 */
export const QR_QUEUE_FILTER: { label: string; value: string }[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: "จ่ายแล้วไม่มีบิล", value: "UNCLAIMED" },
  { label: "ยกเลิกแล้วแต่มีเงินเข้า", value: "PAID_AFTER_CANCEL" },
  { label: "รอคืนเงิน", value: "REFUND_REQUESTED" },
];

const STATUS_OPTIONS = (
  Object.keys(QR_PAYMENT_STATUS_LABEL) as QrPaymentStatus[]
).map((status) => ({ label: QR_PAYMENT_STATUS_LABEL[status], value: status }));

const QUEUE_LABEL = "คิวงาน";
const STATUS_LABEL = "สถานะ";
const BRANCH_LABEL = "สาขา";

/** The label beside a select, given to the select itself as its accessible name. */
const namedSelect = (label: string) => ({ htmlInput: { "aria-label": label } });

/** The calendar's own reach back (DEFAULT_LOOKBACK_YEARS), in the days ทั้งหมดที่มี counts. */
const daysBackToCalendarStart = (today: Dayjs): number =>
  today.diff(today.subtract(DEFAULT_LOOKBACK_YEARS, "year"), "day");

/** ทั้งหมดที่มี reaches as far back as the calendar itself, which covers every QR ever raised. */
const ALL_TIME_LOOKBACK_DAYS = daysBackToCalendarStart(dayjs().startOf("day"));

/**
 * ทั้งหมดที่มี, the range the list opens on: a refund ordered on any date has to be on the first
 * screen, not behind a date someone has to think to widen.
 */
export const ALL_TIME_PRESET = allTimePreset(ALL_TIME_LOOKBACK_DAYS);

const PRESET_GROUPS = defaultPresetGroups({ allTimeLookbackDays: ALL_TIME_LOOKBACK_DAYS });

interface QrTransactionFilterProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  onChangeDateRange: (from: Dayjs, to: Dayjs) => void;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  queue: string;
  setQueue: Dispatch<SetStateAction<string>>;
  branchUuid: string;
  setBranchUuid: Dispatch<SetStateAction<string>>;
}

/**
 * วันที่ · คิวงาน · สถานะ · สาขา, laid out like แดชบอร์ด QR's row: each label sits beside its
 * control. That label is a plain Typography, not associated with the combobox, so each select
 * also carries its label as an aria-label — what assistive tech reads and the tests find it by.
 */
export default function QrTransactionFilter({
  dateFrom,
  dateTo,
  onChangeDateRange,
  status,
  setStatus,
  queue,
  setQueue,
  branchUuid,
  setBranchUuid,
}: Readonly<QrTransactionFilterProps>) {
  const { isHeadOfficeAccounting, isMaster, isAdmin } = useUserRole();
  // Mirrors the server's own cross-branch read cohort. Hiding the field for everyone else
  // is a courtesy, not the guard: the server pins them to their own branch and ignores a
  // branch they name, so a hand-written URL gains nothing.
  const canSelectBranch = Boolean(
    isHeadOfficeAccounting || isMaster || isAdmin,
  );
  const [branches, setBranches] = useState<Branch[]>([]);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data ?? []);
  }, []);

  useEffect(() => {
    if (canSelectBranch) onGetAllBranch();
  }, [canSelectBranch, onGetAllBranch]);

  const handleChangeQueue = (event: ChangeEvent<HTMLInputElement>) =>
    setQueue(event.target.value);
  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) =>
    setStatus(event.target.value);
  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) =>
    setBranchUuid(event.target.value);

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="วันที่">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChangeDateRange}
          presetGroups={PRESET_GROUPS}
        />
      </FilterField>

      <FilterField label={QUEUE_LABEL}>
        <TextField
          select
          size="small"
          fullWidth
          value={queue}
          onChange={handleChangeQueue}
          slotProps={namedSelect(QUEUE_LABEL)}
        >
          {QR_QUEUE_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      <FilterField label={STATUS_LABEL}>
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={handleChangeStatus}
          slotProps={namedSelect(STATUS_LABEL)}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      {canSelectBranch && (
        <FilterField label={BRANCH_LABEL}>
          <TextField
            select
            size="small"
            fullWidth
            value={branchUuid}
            onChange={handleChangeBranch}
            slotProps={namedSelect(BRANCH_LABEL)}
          >
            <MenuItem value="all">ทุกสาขา</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.uuid}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
    </Grid>
  );
}
